import { useShareDownload } from '@/hooks/useShareDownload'
import { formatBytes } from '@/lib/utils'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { createFileRoute } from '@tanstack/react-router'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Logo,
} from '@droidmap/frontend-ui'
import { Button, ProgressBar, Spinner } from '@droidmap/frontend-ui'

// Top-level (like /404), deliberately NOT under _unauthenticated: that
// group's layout redirects authenticated users to `/`, and a share link must
// open for logged-in and anonymous visitors alike. No auth gate, no nav
// chrome — the share token in the path is the credential.
export const Route = createFileRoute('/share/$token')({
  component: SharePage,
})

const formatExpiry = (d: Date | string): string =>
  new Date(d).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export function SharePage() {
  const { token } = Route.useParams()
  const {
    shareInfo,
    isLoadingInfo,
    isLinkUnavailable,
    startDownload,
    isDownloading,
    downloadJob,
    progress,
    error,
  } = useShareDownload(token)

  return (
    <div className="bg-bg flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Logo className="mx-auto h-20 w-auto" />
        <div className="mt-8">
          <Card>
            {isLoadingInfo ? (
              <CardContent className="flex justify-center py-12">
                <Spinner />
              </CardContent>
            ) : isLinkUnavailable || !shareInfo ? (
              <>
                <CardHeader className="text-center">
                  <CardTitle>This link is no longer available</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-text/75">
                    The share link may have expired or been revoked. Ask the
                    person who shared it with you for a new one.
                  </p>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="text-center">
                  <CardTitle className="break-words">
                    {shareInfo.assetSetName}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-text-dim mb-6 text-center text-sm">
                    <span className="font-medium">
                      {shareInfo.assetCount}{' '}
                      {shareInfo.assetCount === 1 ? 'file' : 'files'}
                    </span>
                    {' · '}
                    <span className="font-medium">
                      {formatBytes(shareInfo.totalBytes, 1)}
                    </span>
                    <div className="text-text-mute mt-1 text-xs">
                      Link expires {formatExpiry(shareInfo.expiresAt)}
                    </div>
                  </div>

                  {isDownloading ? (
                    <div className="space-y-3">
                      <div className="text-text-dim text-center text-sm">
                        {progress
                          ? `Preparing your download… ${progress.processedAssets}/${progress.totalAssets} files`
                          : 'Preparing your download…'}
                      </div>
                      <ProgressBar currentProgress={progress?.percent ?? 0} />
                      <div className="text-text-mute text-center text-xs">
                        Large asset sets can take a few minutes to package. Your
                        download will start automatically.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Button size="wide" onClick={startDownload}>
                        <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                        Download ZIP
                      </Button>
                      {downloadJob?.downloadUrl && (
                        <div className="text-text-mute text-center text-xs">
                          Download didn&apos;t start?{' '}
                          <a
                            href={downloadJob.downloadUrl}
                            className="text-accent hover:underline"
                          >
                            Click here
                          </a>
                        </div>
                      )}
                      {error && (
                        <div className="text-danger text-center text-sm">
                          {error.message ||
                            'Something went wrong. Please try again.'}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
